import { useMemo } from 'react'

import {
  type FormFieldDescriptor,
  type NarrowFieldsByValueType,
  type TidalFormState,
  type ValidationSummaryItem,
  useAddFieldValidation,
} from '@workwave-tidal/core/form'

import { useTexts } from './useTexts'
import { specialCharacters as defaultSpecialCharacters } from './specialCharacters'
import { createPasswordValidator } from './createPasswordValidator'

type Texts = ReturnType<typeof useTexts>

type Options = {
  specialCharacters: string[]
  customTexts: Texts
}

export function useStandardPasswordValidations<
  TargetField extends NarrowFieldsByValueType<string, State, FormFieldDescriptor<State>>,
  State extends TidalFormState,
>(field: TargetField, options?: Partial<Options>) {
  const defaultTexts = useTexts()

  const specialCharacters = options?.specialCharacters || defaultSpecialCharacters
  const texts = options?.customTexts || defaultTexts

  const { validationSummaryItemsId, validations, validationSummaryItems } = useMemo(() => {
    const validationSummaryItemsId = {
      'too_short': `${field as string}_too_short`,
      'got_number': `${field as string}_got_number`,
      'got_uppercase_letter': `${field as string}_got_uppercase_letter`,
      'got_lowercase_letter': `${field as string}_got_lowercase_letter`,
      'got_special_character': `${field as string}_got_special_character`,
    }

    const validationSummaryItems = [
      {
        ids: [validationSummaryItemsId.too_short],
        field,
        message: texts.passwordLengthValidation,
      },
      {
        ids: [validationSummaryItemsId.got_number],
        field,
        message: texts.passwordNumberValidation,
      },
      {
        ids: [validationSummaryItemsId.got_uppercase_letter],
        field,
        message: texts.passwordUppercaseValidation,
      },
      {
        ids: [validationSummaryItemsId.got_lowercase_letter],
        field,
        message: texts.passwordLowercaseValidation,
      },
      {
        ids: [validationSummaryItemsId.got_special_character],
        field,
        message: texts.passwordSpecialCharacterValidation(specialCharacters.join('')),
      },
    ] as ValidationSummaryItem<State>[]

    return {
      specialCharacters,
      validationSummaryItems,
      validationSummaryItemsId,
      validations: createPasswordValidator(specialCharacters),
    }
  }, [texts, field])

  useAddFieldValidation<TargetField, State>(
    field,
    (field, _formApi) => {
      return !validations.validateTooShort(field.value as string)
    },
    {
      customUID: validationSummaryItemsId.too_short,
    },
  )

  useAddFieldValidation<TargetField, State>(
    field,
    (field, _formApi) => {
      return validations.validateNoNumbers(field.value as string)
    },
    {
      customUID: validationSummaryItemsId.got_number,
    },
  )

  useAddFieldValidation<TargetField, State>(
    field,
    (field, _formApi) => {
      return validations.validateNoUppercaseLetter(field.value as string)
    },
    {
      customUID: validationSummaryItemsId.got_uppercase_letter,
    },
  )

  useAddFieldValidation<TargetField, State>(
    field,
    (field, _formApi) => {
      return validations.validateNoLowercaseLetter(field.value as string)
    },
    {
      customUID: validationSummaryItemsId.got_lowercase_letter,
    },
  )

  useAddFieldValidation<TargetField, State>(
    field,
    (field, _formApi) => {
      return validations.validateNoSpecialCharacter(field.value as string)
    },
    {
      customUID: validationSummaryItemsId.got_special_character,
    },
  )

  return validationSummaryItems
}
